import { isDefined } from '@sgme/fp';
import classNames from 'classnames';
import { useField } from 'components/common-form/hooks/useField';
import { useFieldTranslationKey } from 'components/common-form/hooks/useFieldTranslationKey';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { LabelWithTooltip } from 'components/shared/LabelWithTooltip/LabelWithTooltip';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';

export const CashFlowField: FC<ProductNodeBuilderProps> = ({ node: { id } }) => {
  const { formatMessage } = useIntl();
  const { metadata, data } = useField<'CashFlowField'>(id);
  const translationKey = useFieldTranslationKey(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);

  const displayValue = useMemo(() => {
    return isDefined(data) ? Math.abs(data) : undefined;
  }, [data]);

  const styles = useMemo(() => {
    if (data === null) {
      return undefined;
    }

    if (data && data > 0) {
      return {
        icon: 'error',
        color: 'text-warning',
        message: formatMessage({ id: 'field.CashFlow.positive' }),
      };
    }

    if (data && data < 0) {
      return {
        icon: 'check_circle',
        color: 'text-success',
        message: formatMessage({ id: 'field.CashFlow.negative' }),
      };
    }

    return {
      icon: 'check_circle',
      color: 'text-success',
      message: formatMessage({ id: 'field.CashFlow.negative' }),
    };
  }, [data, formatMessage]);

  const label = styles ? `${styles.message} ${displayValue?.toFixed(metadata?.nbDecimals)}%` : '-';

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${translationKey}` as any}
      minWidth={metadata?.minWidth}
      maxWidth={metadata?.maxWidth}
      className={patchedFieldClassName}
    >
      <div
        style={{ height: 32 }}
        className={classNames('d-flex align-items-center px-2', styles?.color)}
        data-e2e="cash-flow-field"
      >
        {styles?.icon && <i className="icon icon-sm me-1 align-content-center">{styles?.icon}</i>}
        <LabelWithTooltip label={label} tooltip={label} className={styles?.color} />
      </div>
    </BlockOrientationFormGroup>
  );
};

CashFlowField.displayName = 'CashFlowField';
